import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatErr } from "../lib/api";
import { motion } from "framer-motion";

export function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const u = await login(email, password);
      // Respect return URL (e.g. coming from /book/:id) for customers only
      const returnTo = sp.get("return");
      if (u.role === "customer" && returnTo && returnTo.startsWith("/")) {
        nav(returnTo);
      } else {
        nav(u.role === "admin" ? "/admin" : (u.role === "business" || u.role === "staff_user") ? "/business" : "/me");
      }
    } catch (ex) {
      setErr(formatErr(ex.response?.data?.detail) || ex.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthShell title="Bentornato" subtitle="Accedi per gestire le tue prenotazioni.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div>
          <label className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Email</label>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input-soft mt-2" data-testid="login-email" />
        </div>
        <div>
          <label className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Password</label>
          <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="input-soft mt-2" data-testid="login-password" />
        </div>
        {err && <div className="text-sm text-red-600" data-testid="login-error">{err}</div>}
        <button type="submit" disabled={loading} className="btn-accent w-full" data-testid="login-submit">
          {loading ? "Accesso…" : "Accedi"}
        </button>
        <div className="text-center">
          <Link to="/forgot-password" className="text-sm text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100" data-testid="login-forgot">Password dimenticata?</Link>
        </div>
        <div className="text-center text-sm text-zinc-500 dark:text-zinc-400">
          Non hai un account? <Link to="/register" className="text-zinc-900 font-medium dark:text-zinc-100">Registrati</Link>
        </div>
      </form>
    </AuthShell>
  );
}

export function RegisterPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [privacy, setPrivacy] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setErr("");
    if (!privacy) { setErr("Devi accettare l'Informativa Privacy per continuare."); return; }
    setLoading(true);
    try {
      await register({ email, password, name, role: "customer", privacy_accepted: true });
      nav("/me");
    } catch (ex) {
      setErr(formatErr(ex.response?.data?.detail) || ex.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthShell title="Crea account cliente" subtitle="Crea l'account per prenotare e gestire i tuoi appuntamenti.">
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div>
          <label className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Nome</label>
          <input required value={name} onChange={(e) => setName(e.target.value)} className="input-soft mt-2" data-testid="register-name" />
        </div>
        <div>
          <label className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Email</label>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input-soft mt-2" data-testid="register-email" />
        </div>
        <div>
          <label className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Password</label>
          <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="input-soft mt-2" data-testid="register-password" />
        </div>
        <label className="flex items-start gap-2 text-xs text-zinc-600 select-none cursor-pointer dark:text-zinc-400" data-testid="register-privacy-label">
          <input type="checkbox" checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} className="mt-0.5 h-4 w-4 accent-blue-600" data-testid="register-privacy-checkbox" />
          <span>
            Dichiaro di aver letto e di accettare l'
            <Link to="/privacy" target="_blank" className="text-blue-600 hover:underline">Informativa Privacy</Link>
            {" "}ex art. 13 GDPR e i{" "}
            <Link to="/terms" target="_blank" className="text-blue-600 hover:underline">Termini di Servizio</Link>.
          </span>
        </label>
        {err && <div className="text-sm text-red-600" data-testid="register-error">{err}</div>}
        <button type="submit" disabled={loading} className="btn-accent w-full" data-testid="register-submit">
          {loading ? "Creazione…" : "Crea account"}
        </button>
        <div className="text-center text-xs text-zinc-500 pt-2 border-t border-zinc-100 mt-3 dark:text-zinc-400 dark:border-zinc-800">
          Sei un'attività? Gli account business vengono attivati solo dopo contatto diretto con noi.
        </div>
        <div className="text-center text-sm text-zinc-500 dark:text-zinc-400">
          Hai già un account? <Link to="/login" className="text-zinc-900 font-medium dark:text-zinc-100">Accedi</Link>
        </div>
      </form>
    </AuthShell>
  );
}

function AuthShell({ title, subtitle, children }) {
  return (
    <div className="max-w-md mx-auto px-4 py-16">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <h1 className="font-display text-3xl font-medium tracking-tight">{title}</h1>
        <p className="text-zinc-500 mt-2 dark:text-zinc-400">{subtitle}</p>
        <div className="card-soft p-6 mt-8">{children}</div>
      </motion.div>
    </div>
  );
}
